// Members page — per-project membership management.
//
// Read access: every project member can see who's on the project. Write
// access: admin / pm in the project (the store's `role` mirrors the
// per-project role when inside a project). Per the role-cap rule, a PM
// cannot grant or modify roles at admin level — only org-level admins can.
//
// The "Add member" picker lists existing org users (admin creates them
// in the admin console, never here) who aren't already on the project.

import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useStore } from '../store/useStore';
import {
  getProjectMembers,
  addProjectMember,
  updateProjectMemberRole,
  removeProjectMember,
} from '../utils/api';
import styles from './ProjectMembers.module.css';

const ALL_ROLES = [
  { value: 'admin', label: 'Admin' },
  { value: 'pm',    label: 'PM' },
  { value: 'super', label: 'Super' },
  { value: 'field', label: 'Field' },
];
const ROLE_RANK = { field: 0, super: 1, pm: 2, admin: 3 };

// Caller can assign at or below their own role. Admin → admin; PM → pm.
// Super / Field cannot assign roles at all.
function maxAssignableBy(role) {
  if (role === 'admin') return 'admin';
  if (role === 'pm')    return 'pm';
  return null;
}

function rolesAtOrBelow(cap) {
  if (!cap) return [];
  return ALL_ROLES.filter((r) => ROLE_RANK[r.value] <= ROLE_RANK[cap]);
}

const ROLE_LABEL = Object.fromEntries(ALL_ROLES.map((r) => [r.value, r.label]));

export default function ProjectMembers() {
  const { project, role: callerRole, currentUser, users: orgUsers } = useStore();

  const [members,  setMembers]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [busyId,   setBusyId]   = useState(null);     // user id currently being mutated
  const [showAdd,  setShowAdd]  = useState(false);
  const [newUserId, setNewUserId] = useState('');
  const [newRole,   setNewRole]   = useState('field');

  const cap = maxAssignableBy(callerRole);
  const canManage = !!cap;
  const assignableRoles = rolesAtOrBelow(cap);

  async function refresh() {
    if (!project) return;
    try {
      setMembers(await getProjectMembers(project.id));
    } catch (err) {
      toast.error(err.message || 'Failed to load members');
    }
  }

  useEffect(() => {
    if (!project) return;
    setLoading(true);
    refresh().finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project?.id]);

  // Keep the new-member role default in sync with what the caller can grant.
  useEffect(() => {
    if (!cap) return;
    if (ROLE_RANK[newRole] > ROLE_RANK[cap]) {
      setNewRole(cap === 'pm' ? 'field' : 'field');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cap]);

  // Org users who aren't already members of this project — these are the
  // candidates the "Add member" picker shows.
  const memberIds = useMemo(() => new Set(members.map((m) => m.id)), [members]);
  const candidates = useMemo(
    () => (orgUsers || []).filter((u) => !memberIds.has(u.id)),
    [orgUsers, memberIds]
  );

  async function handleAdd() {
    if (!newUserId) return;
    setBusyId(newUserId);
    try {
      await addProjectMember(project.id, { user_id: newUserId, role: newRole });
      toast.success('Member added');
      setShowAdd(false);
      setNewUserId('');
      setNewRole('field');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Add failed');
    } finally {
      setBusyId(null);
    }
  }

  async function handleRoleChange(memberId, newRole) {
    setBusyId(memberId);
    try {
      await updateProjectMemberRole(project.id, memberId, newRole);
      toast.success(`Role set to ${ROLE_LABEL[newRole]}`);
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Update failed');
      await refresh(); // pull fresh state in case the optimistic UI was wrong
    } finally {
      setBusyId(null);
    }
  }

  async function handleRemove(memberId) {
    if (!window.confirm('Remove this member from the project?')) return;
    setBusyId(memberId);
    try {
      await removeProjectMember(project.id, memberId);
      toast.success('Member removed');
      await refresh();
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || 'Remove failed');
    } finally {
      setBusyId(null);
    }
  }

  if (!project) return null;

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerLeft}>
          <h1 className={styles.title}>MEMBERS</h1>
          <span className={styles.count}>
            {members.length} {members.length === 1 ? 'person' : 'people'}
          </span>
        </div>
        {canManage && (
          <button
            className={styles.addBtn}
            onClick={() => setShowAdd((v) => !v)}
            disabled={!candidates.length}
            title={!candidates.length ? 'Every org user is already on this project' : 'Add an existing org user'}
          >
            {showAdd ? 'CANCEL' : '+ ADD MEMBER'}
          </button>
        )}
      </div>

      {showAdd && canManage && (
        <div className={styles.addRow}>
          <select
            className={styles.select}
            value={newUserId}
            onChange={(e) => setNewUserId(e.target.value)}
          >
            <option value="">Pick a user…</option>
            {candidates.map((u) => (
              <option key={u.id} value={u.id}>{u.name} — {u.email}</option>
            ))}
          </select>
          <select
            className={styles.select}
            value={newRole}
            onChange={(e) => setNewRole(e.target.value)}
          >
            {assignableRoles.map((r) => (
              <option key={r.value} value={r.value}>{r.label}</option>
            ))}
          </select>
          <button
            className={styles.confirmBtn}
            onClick={handleAdd}
            disabled={!newUserId || busyId === newUserId}
          >
            ADD
          </button>
        </div>
      )}

      {loading ? (
        <div className={styles.loading}>Loading…</div>
      ) : members.length === 0 ? (
        <div className={styles.empty}>No members yet.</div>
      ) : (
        <div className={styles.tableWrap}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Added</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {members.map((m) => {
                const isMe = currentUser?.id === m.id;
                const targetAtOrBelowCap = cap && ROLE_RANK[m.role] <= ROLE_RANK[cap];
                const canEditThisRow = canManage && targetAtOrBelowCap;
                const busy = busyId === m.id;
                return (
                  <tr key={m.id} className={busy ? styles.rowBusy : ''}>
                    <td className={styles.nameCell}>
                      {m.name}
                      {isMe && <span className={styles.youTag}>(you)</span>}
                    </td>
                    <td className={styles.emailCell}>{m.email}</td>
                    <td>
                      {canEditThisRow ? (
                        <select
                          className={styles.roleSelect}
                          value={m.role}
                          onChange={(e) => handleRoleChange(m.id, e.target.value)}
                          disabled={busy}
                        >
                          {assignableRoles.map((r) => (
                            <option key={r.value} value={r.value}>{r.label}</option>
                          ))}
                          {/* If the row's current role is above the cap, show it
                              as a disabled option so we don't render a select
                              with an unsupported value. */}
                          {!assignableRoles.find((r) => r.value === m.role) && (
                            <option key={m.role} value={m.role} disabled>
                              {ROLE_LABEL[m.role] || m.role}
                            </option>
                          )}
                        </select>
                      ) : (
                        <span className={`${styles.roleBadge} ${styles[`role_${m.role}`] || ''}`}>
                          {ROLE_LABEL[m.role] || m.role}
                        </span>
                      )}
                    </td>
                    <td className={styles.dateCell}>
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className={styles.actionCell}>
                      {canEditThisRow && !isMe && (
                        <button
                          className={styles.removeBtn}
                          onClick={() => handleRemove(m.id)}
                          disabled={busy}
                          title="Remove from project"
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {!canManage && (
        <p className={styles.note}>
          Only project admins and PMs can manage membership. Talk to one to be added or have your role changed.
        </p>
      )}
    </div>
  );
}
